import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import {
    Button,
    Chip,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TextField,
    Typography,
} from "@mui/material";
import Popup from "../../../components/Popup";
import { StyledTableCell, StyledTableRow } from "../../../components/styles";
import { getApiBaseUrl } from "../../../config/api";

const formatDate = (d) => new Date(d).toLocaleDateString("en-GB");

const STATUS_OPTIONS = ["All", "Pending", "Approved", "Rejected"];

const STATUS_CHIP = {
    Pending: "warning",
    Approved: "success",
    Rejected: "error",
};

function truncateReason(text, max = 50) {
    const t = String(text || "").trim();
    if (!t) return "—";
    return t.length > max ? `${t.slice(0, max)}…` : t;
}

const LeaveApprovals = () => {
    const { currentUser } = useSelector((s) => s.user);
    const schoolId = currentUser?._id;

    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [statusFilter, setStatusFilter] = useState("All");

    const [showPopup, setShowPopup] = useState(false);
    const [message, setMessage] = useState("");

    const [rejectOpen, setRejectOpen] = useState(false);
    const [rejectTargetId, setRejectTargetId] = useState(null);
    const [rejectionNote, setRejectionNote] = useState("");
    const [actionLoading, setActionLoading] = useState(false);

    const loadLeaves = useCallback(async () => {
        if (!schoolId) return;
        setLoading(true);
        try {
            const params = statusFilter !== "All" ? `?status=${encodeURIComponent(statusFilter)}` : "";
            const { data } = await axios.get(`${getApiBaseUrl()}/Leaves/school/${schoolId}${params}`);
            if (data?.message && !Array.isArray(data)) {
                setRows([]);
            } else {
                setRows(Array.isArray(data) ? data : []);
            }
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not load leave applications");
            setShowPopup(true);
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [schoolId, statusFilter]);

    useEffect(() => {
        loadLeaves();
    }, [loadLeaves]);

    const summary = useMemo(() => {
        const total = rows.length;
        const pending = rows.filter((r) => r.status === "Pending").length;
        const approved = rows.filter((r) => r.status === "Approved").length;
        return { total, pending, approved };
    }, [rows]);

    const notifySuccess = () => {
        setMessage("Completed successfully!");
        setShowPopup(true);
    };

    const handleApprove = async (id) => {
        if (!window.confirm("Approve this leave application?")) return;
        setActionLoading(true);
        try {
            await axios.put(`${getApiBaseUrl()}/Leave/${id}/approve`, {
                approvedById: schoolId,
            });
            notifySuccess();
            await loadLeaves();
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not approve leave");
            setShowPopup(true);
        } finally {
            setActionLoading(false);
        }
    };

    const openReject = (id) => {
        setRejectTargetId(id);
        setRejectionNote("");
        setRejectOpen(true);
    };

    const handleReject = async () => {
        if (!rejectTargetId) return;
        setActionLoading(true);
        try {
            await axios.put(`${getApiBaseUrl()}/Leave/${rejectTargetId}/reject`, {
                rejectionNote: rejectionNote.trim(),
            });
            notifySuccess();
            setRejectOpen(false);
            setRejectTargetId(null);
            await loadLeaves();
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not reject leave");
            setShowPopup(true);
        } finally {
            setActionLoading(false);
        }
    };

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 2, md: 3 } }}>
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems="flex-start" spacing={2} sx={{ mb: 2 }}>
                <Typography variant="h5" fontWeight={700}>
                    Teacher Leave Applications
                </Typography>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Status</InputLabel>
                    <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                        {STATUS_OPTIONS.map((s) => (
                            <MenuItem key={s} value={s}>
                                {s}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Stack>

            <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <Typography variant="caption" color="text.secondary">
                            Total applications
                        </Typography>
                        <Typography variant="h6" fontWeight={700}>
                            {summary.total}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Typography variant="caption" color="text.secondary">
                            Pending
                        </Typography>
                        <Typography variant="h6" fontWeight={700} color="warning.main">
                            {summary.pending}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Typography variant="caption" color="text.secondary">
                            Approved
                        </Typography>
                        <Typography variant="h6" fontWeight={700} color="success.main">
                            {summary.approved}
                        </Typography>
                    </Grid>
                </Grid>
            </Paper>

            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
                <TableContainer sx={{ overflowX: "auto" }}>
                    <Table size="small">
                        <TableHead>
                            <StyledTableRow>
                                <StyledTableCell>Teacher</StyledTableCell>
                                <StyledTableCell>Leave type</StyledTableCell>
                                <StyledTableCell>Start</StyledTableCell>
                                <StyledTableCell>End</StyledTableCell>
                                <StyledTableCell>Days</StyledTableCell>
                                <StyledTableCell>Reason</StyledTableCell>
                                <StyledTableCell>Status</StyledTableCell>
                                <StyledTableCell align="right">Actions</StyledTableCell>
                            </StyledTableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <StyledTableRow>
                                    <StyledTableCell colSpan={8}>
                                        <Skeleton height={48} />
                                    </StyledTableCell>
                                </StyledTableRow>
                            ) : rows.length === 0 ? (
                                <StyledTableRow>
                                    <StyledTableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            No leave applications found.
                                        </Typography>
                                    </StyledTableCell>
                                </StyledTableRow>
                            ) : (
                                rows.map((row) => {
                                    const id = row.id || row._id;
                                    const status = row.status || "Pending";
                                    return (
                                        <StyledTableRow key={id} hover>
                                            <StyledTableCell>{row.teacher?.name || "—"}</StyledTableCell>
                                            <StyledTableCell>{row.leaveType}</StyledTableCell>
                                            <StyledTableCell>{formatDate(row.startDate)}</StyledTableCell>
                                            <StyledTableCell>{formatDate(row.endDate)}</StyledTableCell>
                                            <StyledTableCell>{row.numberOfDays}</StyledTableCell>
                                            <StyledTableCell>{truncateReason(row.reason)}</StyledTableCell>
                                            <StyledTableCell>
                                                <Chip label={status} size="small" color={STATUS_CHIP[status] || "default"} />
                                            </StyledTableCell>
                                            <StyledTableCell align="right">
                                                {status === "Pending" && (
                                                    <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="wrap">
                                                        <Button
                                                            size="small"
                                                            variant="contained"
                                                            color="success"
                                                            disabled={actionLoading}
                                                            onClick={() => handleApprove(id)}
                                                        >
                                                            Approve
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            color="error"
                                                            disabled={actionLoading}
                                                            onClick={() => openReject(id)}
                                                        >
                                                            Reject
                                                        </Button>
                                                    </Stack>
                                                )}
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Reject leave application</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        label="Rejection note"
                        value={rejectionNote}
                        onChange={(e) => setRejectionNote(e.target.value)}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRejectOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleReject} disabled={actionLoading}>
                        Reject
                    </Button>
                </DialogActions>
            </Dialog>

            <Popup message={message} showPopup={showPopup} setShowPopup={setShowPopup} />
        </Container>
    );
};

export default LeaveApprovals;
